<template>
  <div class="admin-authors-container fade-in">

    <!-- ── Page Header ─────────────────────────────────────── -->
    <div class="page-header mb-4">
      <div class="page-header-left">
        <h2 class="page-title">Quản lý Tác giả</h2>
        <p class="page-subtitle">Cấu hình danh mục tác giả viết sách</p>
      </div>
      <button @click="openAddModal" class="btn-book-primary-pill">
        <icon icon="fa-solid fa-plus" />
        <span>Thêm tác giả</span>
      </button>
    </div>

    <!-- ── Authors Table Panel ─────────────────────────────── -->
    <div class="dashboard-panel">
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">Danh sách tác giả</h3>
          <span class="panel-subtitle">Tổng số: {{ filteredAuthors.length }} tác giả</span>
        </div>
        <div class="panel-actions-wrapper">
          <div class="search-box-panel">
            <input
              :value="searchQuery"
              type="text"
              placeholder="Tìm tác giả..."
              class="panel-search-input"
              @input="onSearchInput($event.target.value)"
              @keyup.enter="searchNow"
            />
            <icon icon="fa-solid fa-magnifying-glass" class="panel-search-icon" />
          </div>
        </div>
      </div>

      <!-- ── Pagination (Top) ────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper pagination-top">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> tác giả
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="col-stt text-center">STT</th>
              <th>Tên Tác Giả</th>
              <th class="text-center col-lifespan">Năm Sinh / Mất</th>
              <th class="text-center col-book-count">Số lượng sách</th>
              <th class="text-center col-status">
                Trạng Thái
                <icon 
                  icon="fa-solid fa-circle-question" 
                  class="help-tooltip-icon" 
                  data-tooltip-pos="bottom" 
                  data-tooltip="Ý nghĩa màu sắc:
                  • Trắng: Đang phát hành
                  • Vàng: Đang chờ xóa
                  • Xanh lam: Đang chờ khôi phục
                  • Đỏ: Đã xóa" 
                />
              </th>
              <th class="text-center col-actions">Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            <!-- Skeleton Loader -->
            <template v-if="isLoading">
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td class="text-center">
                  <span class="skeleton-bar skeleton-text-stt"></span>
                </td>
                <td>
                  <span class="skeleton-bar skeleton-text-name"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-text-lifespan"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr v-for="(auth, index) in filteredAuthors" :key="auth.id" class="table-row-hover">
                <td class="col-stt text-center text-muted fw-semibold">{{ currentPage * pageSize + index + 1 }}</td>
                <td>
                  <span class="row-name">{{ auth.name }}</span>
                </td>
                <td class="text-center">
                  <span class="text-muted-dark fs-8-5">{{ formatLifespan(auth.yob, auth.yod) }}</span>
                </td>
                <td class="text-center">
                  <span class="count-pill" :class="{ 'count-pill-zero': !auth.bookCount }">
                    <icon icon="fa-solid fa-book" class="count-pill-icon" />
                    <span>{{ auth.bookCount || 0 }} sách</span>
                  </span>
                </td>
                <td class="text-center">
                  <span
                    class="status-badge-custom"
                    :class="getStatusBadgeClass(auth)"
                    :data-tooltip="getStatusTooltip(auth)"
                  >
                    {{ getStatusLabel(auth) }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="action-btn-group">
                    <template v-if="auth.status !== 2">
                      <button @click="openEditModal(auth)" class="btn-action-icon btn-action-edit" data-tooltip="Chỉnh sửa">
                        <icon icon="fa-solid fa-pen-to-square" />
                      </button>
                    </template>
                    <button @click="openHistoryModal(auth)" class="btn-action-icon btn-action-history" data-tooltip="Xem lịch sử">
                      <icon icon="fa-solid fa-clock-rotate-left" />
                    </button>
                    <template v-if="auth.status !== 2">
                      <button @click="confirmDelete(auth)" class="btn-action-icon btn-action-delete" data-tooltip="Xóa bỏ">
                        <icon icon="fa-solid fa-trash-can" />
                      </button>
                    </template>
                    <template v-else>
                      <button @click="confirmRestore(auth)" class="btn-action-icon btn-action-restore" :data-tooltip="isAdmin ? 'Khôi phục' : 'Yêu cầu khôi phục'">
                        <icon icon="fa-solid fa-trash-can-arrow-up" />
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredAuthors.length === 0">
                <td colspan="6" class="empty-state-row">
                  <icon icon="fa-solid fa-inbox" class="empty-icon" />
                  <span>Không tìm thấy tác giả nào.</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ──────────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> tác giả
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

    </div>

    <!-- ── Add / Edit Modal ────────────────────────────────── -->
    <teleport to="body">
      <div v-if="modalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon :icon="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" />
              {{ isEditMode ? 'Cập nhật tác giả' : 'Thêm tác giả mới' }}
            </h3>
            <button @click="closeModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <div v-if="isModalLoading" class="text-center py-5">
              <span class="text-muted"><icon icon="fa-solid fa-spinner" class="fa-spin" /> Đang tải thông tin chi tiết...</span>
            </div>
            <template v-else>
              <!-- Tên tác giả -->
              <div class="form-field mb-3">
                <label class="form-field-label">Tên tác giả <span class="required-mark">*</span></label>
                <input
                  v-model="form.name"
                  type="text"
                  class="custom-input-modal"
                  :class="{ 'input-error': errors.name }"
                  placeholder="Ví dụ: Dale Carnegie, Nguyễn Du..."
                  ref="nameInput"
                />
                <span v-if="errors.name" class="field-error-msg">{{ errors.name }}</span>
              </div>

              <!-- Năm sinh / Năm mất -->
              <div class="form-row-2col mb-3">
                <div class="form-field">
                  <label class="form-field-label">Năm sinh</label>
                  <input
                    v-model.number="form.yob"
                    type="number"
                    class="custom-input-modal"
                    :class="{ 'input-error': errors.yob }"
                    placeholder="Ví dụ: 1888"
                  />
                  <span v-if="errors.yob" class="field-error-msg">{{ errors.yob }}</span>
                </div>
                <div class="form-field">
                  <label class="form-field-label">Năm mất (nếu có)</label>
                  <input
                    v-model.number="form.yod"
                    type="number"
                    class="custom-input-modal"
                    :class="{ 'input-error': errors.yod }"
                    placeholder="Để trống nếu còn sống"
                  />
                  <span v-if="errors.yod" class="field-error-msg">{{ errors.yod }}</span>
                </div>
              </div>

              <!-- Danh sách gợi ý trùng lặp (nếu có) -->
              <div v-if="similarAuthors && similarAuthors.length" class="similar-suggestions-container mb-3">
                <span class="suggestions-title">💡 Tác giả trùng hoặc gần giống trong hệ thống:</span>
                <div class="suggestions-list">
                  <span
                    v-for="sa in similarAuthors"
                    :key="sa.id"
                    class="suggestion-item"
                    :class="{ 'suggestion-item-exact': sa.name.trim().toLowerCase() === form.name.trim().toLowerCase() }"
                  >
                    {{ sa.name }}
                    <template v-if="sa.yob || sa.yod">
                      ({{ sa.yob || '?' }} - {{ sa.yod || '?' }})
                    </template>
                  </span>
                </div>
                <div v-if="hasExactDuplicate" class="suggestion-match-warning">
                  <icon icon="fa-solid fa-triangle-exclamation" />
                  <span>Tên tác giả này trùng lặp hoàn toàn với tác giả đã có!</span>
                </div>
              </div>
              <div v-if="checkDoneOnce && !hasExactDuplicate && userHasChecked" class="suggestion-clear mb-3">
                <icon icon="fa-solid fa-circle-check" />
                <span>Dữ liệu hợp lệ! Bạn có thể lưu tác giả này.</span>
              </div>
            </template>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeModal" class="btn-modal-cancel">Hủy bỏ</button>
            <button
              v-if="!checkDoneOnce || hasExactDuplicate"
              type="button"
              @click="checkSimilarNow"
              class="btn-modal-check"
              :disabled="isSearchingSimilar || !form.name.trim() || isUnchanged"
            >
              <icon v-if="isSearchingSimilar" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-magnifying-glass" class="me-1" />
              <span>Kiểm tra dữ liệu</span>
            </button>
            <button
              v-else
              @click="saveAuthor"
              class="btn-modal-confirm"
              :disabled="isLoading || isModalLoading || !form.name.trim() || isUnchanged"
            >
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Lưu dữ liệu
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Delete Confirmation Modal ──────────────────────── -->
    <teleport to="body">
      <div v-if="deleteModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-danger">
            <h3 class="modal-title-custom modal-title-danger">
              <icon icon="fa-solid fa-triangle-exclamation" />
              {{ isAdmin ? 'Xác nhận xóa' : 'Gửi yêu cầu xóa tác giả' }}
            </h3>
            <button @click="closeDeleteModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text">
              {{ isAdmin ? 'Bạn có chắc chắn muốn xóa tác giả' : 'Bạn có chắc chắn muốn gửi yêu cầu xóa tác giả' }} <strong>{{ selectedAuthor?.name }}</strong>?
            </p>
            <p class="delete-warn-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ isAdmin ? 'Tác giả sẽ bị gỡ khỏi tất cả sách liên quan. Hành động này không thể hoàn tác.' : 'Yêu cầu xóa sẽ được gửi đến Quản trị viên để xem xét phê duyệt.' }}
            </p>
            <div class="form-field">
              <label class="form-field-label">Lý do xóa <span class="required-mark">*</span></label>
              <textarea
                v-model="deleteReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.deleteReason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Tác giả này trùng lặp với tác giả X đã tạo trước đó..."
                ref="deleteReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.deleteReason" class="field-error-msg m-0">{{ errors.deleteReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ deleteReason ? deleteReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeDeleteModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button
              @click="deleteAuthor"
              class="btn-modal-danger"
              :disabled="isLoading"
            >
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              {{ isAdmin ? 'Xác nhận Xóa' : 'Gửi yêu cầu Xóa' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Restore Modal ────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="restoreModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-restore">
            <h3 class="modal-title-custom modal-title-restore">
              <icon icon="fa-solid fa-trash-can-arrow-up" />
              {{ isAdmin ? 'Khôi phục tác giả' : 'Yêu cầu khôi phục tác giả' }}
            </h3>
            <button @click="closeRestoreModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text">
              {{ isAdmin ? 'Bạn có chắc chắn muốn khôi phục tác giả' : 'Bạn muốn gửi yêu cầu khôi phục tác giả' }} <strong>{{ selectedAuthorForRestore?.name }}</strong>?
            </p>
            <p class="restore-info-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ isAdmin ? 'Tác giả sẽ được chuyển về trạng thái Đang phát hành.' : 'Yêu cầu khôi phục sẽ được chuyển đến Quản trị viên để xét duyệt.' }}
            </p>
            <div class="form-field">
              <label class="form-field-label">
                {{ isAdmin ? 'Lý do khôi phục' : 'Lý do yêu cầu khôi phục' }} <span class="required-mark">*</span>
              </label>
              <textarea
                v-model="restoreReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.restoreReason }"
                :placeholder="isAdmin ? 'Nhập tối thiểu 10 ký tự lý do khôi phục...' : 'Nhập tối thiểu 10 ký tự lý do yêu cầu khôi phục...'"
                ref="restoreReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.restoreReason" class="field-error-msg m-0">{{ errors.restoreReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ restoreReason ? restoreReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeRestoreModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button
              @click="restoreAuthor"
              class="btn-modal-restore"
              :disabled="isLoading || !restoreReason || restoreReason.trim().length < 10 || restoreReason.trim().length > 500"
            >
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-trash-can-arrow-up" class="me-1" />
              {{ isAdmin ? 'Xác nhận Khôi phục' : 'Gửi yêu cầu Khôi phục' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Reason Input Modal ──────────────────────────────── -->
    <teleport to="body">
      <div v-if="reasonModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-circle-question" />
              Lý do chỉnh sửa
            </h3>
            <button @click="closeReasonModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3">Vui lòng cung cấp lý do cập nhật thông tin tác giả để ghi nhận lịch sử thay đổi.</p>
            <div class="form-field">
              <label class="form-field-label">Lý do thay đổi <span class="required-mark">*</span></label>
              <textarea
                v-model="form.reason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.reason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Sửa lại năm sinh của tác giả chính xác theo Wikipedia..."
                ref="reasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.reason" class="field-error-msg m-0">{{ errors.reason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ form.reason ? form.reason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeReasonModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="submitUpdateWithReason" class="btn-modal-confirm" :disabled="isLoading || !form.reason || form.reason.trim().length < 10 || form.reason.trim().length > 500">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── History Modal ──────────────────────────────────── -->
    <teleport to="body">
      <div v-if="historyModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-md">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-clock-rotate-left" />
              Lịch sử thay đổi: {{ selectedAuthorForHistory?.name }}
            </h3>
            <button @click="closeHistoryModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom modal-body-history">
            <div v-if="isHistoryLoading" class="text-center py-4">
              <span class="text-muted"><icon icon="fa-solid fa-spinner" class="fa-spin" /> Đang tải dữ liệu...</span>
            </div>
            <div v-else class="history-timeline">
              <div v-for="entry in historyList" :key="entry.id" class="timeline-item">
                <div class="timeline-badge" :class="getHistoryBadgeClass(entry.type)">
                  <icon :icon="getHistoryIcon(entry.type)" />
                </div>
                <div class="timeline-content-card">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ getHistoryActionLabel(entry.type) }}</span>
                    <span class="timeline-time">{{ formatVersionTime(entry.updateTime) }}</span>
                  </div>
                  <div class="timeline-actor">
                    <icon icon="fa-solid fa-user" />
                    <span>Thực hiện bởi: <strong>{{ entry.updateByName || 'Quản trị viên' }}</strong></span>
                  </div>
                  <!-- Render dynamic details based on type -->
                  <div class="timeline-details-row">
                    <!-- ADD (Khởi tạo) -->
                    <p v-if="entry.type === 1" class="timeline-details">
                      Khởi tạo tác giả mới.
                    </p>

                    <!-- UPDATE (Cập nhật) -->
                    <div v-else-if="entry.type === 2" class="timeline-details">
                      <div v-if="entry.fieldChanges && entry.fieldChanges.length > 0" class="field-changes-list">
                        <div v-for="change in entry.fieldChanges" :key="change.field" class="field-change-item mb-2">
                          <span class="field-label-tag"><strong>{{ change.field === 'name' ? 'Tên tác giả' : change.field === 'yob' ? 'Năm sinh' : change.field === 'yod' ? 'Năm mất' : change.field }}</strong>:</span>
                          <div class="field-values-diff mt-1">
                            <div class="diff-value-box">
                              <span class="badge bg-secondary text-white me-1">Trước:</span>
                              <span class="text-dark">{{ change.oldValue && change.oldValue !== 'null' ? change.oldValue : '(Trống)' }}</span>
                            </div>
                            <div class="diff-value-box mt-1">
                              <span class="badge bg-primary text-white me-1">Sau:</span>
                              <span class="text-dark">{{ change.newValue && change.newValue !== 'null' ? change.newValue : '(Trống)' }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <span v-else>Cập nhật thông tin tác giả.</span>

                      <div v-if="entry.reason" class="mt-2 text-muted-dark" style="font-size: 0.82rem;">
                        Lý do thay đổi: <strong>{{ entry.reason }}</strong>
                      </div>
                    </div>

                    <!-- DELETE (Xóa) -->
                    <p v-else-if="entry.type === 3" class="timeline-details">
                      Yêu cầu xóa tác giả.
                      <template v-if="entry.reason">
                        <br />
                        Lý do: <strong>{{ entry.reason }}</strong>
                      </template>
                    </p>

                    <!-- Fallback -->
                    <p v-else class="timeline-details">
                      Tên tác giả: <strong>{{ entry.name }}</strong>
                      <br />
                      Sinh/Mất: <strong>{{ formatLifespan(entry.yob, entry.yod) }}</strong>
                      <br v-if="entry.reason" />
                      <span v-if="entry.reason">Lý do thay đổi: <strong>{{ entry.reason }}</strong></span>
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="historyList.length === 0" class="history-empty">
                <icon icon="fa-solid fa-clock" />
                <span>Chưa ghi nhận lịch sử thay đổi nào cho tác giả này.</span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeHistoryModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script src="./authors.js"></script>
<style scoped src="./authors.css"></style>
